import React, { useRef, useState } from "react";
import BackButton from "../components/BackButton";
import "./CozeRunner.css";
import { apiFetch } from "../api";

export default function XhsRunner() {
  // ================ 可调参数 ================
  const REVEAL_INTERVAL_MS = 60;
  const MIN_CHUNK_CHARS = 24;
  const MAX_CHUNK_CHARS = 64;
  const DEBUG = (() => {
    try {
      const qs = new URLSearchParams(window.location.search);
      if (qs.has("debug")) return true;
      return localStorage.getItem("xhs_debug") === "1";
    } catch {
      return false;
    }
  })();

  // ================ UI 状态 ================
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [title, setTitle] = useState("");
  const [link, setLink] = useState("");
  const [chunks, setChunks] = useState([]);
  const [streamRaw, setStreamRaw] = useState("");
  const [debugLines, setDebugLines] = useState([]);
  const [rawInput, setRawInput] = useState("");
  const [sentUrl, setSentUrl] = useState("");
  const [lastEventRaw, setLastEventRaw] = useState("");
  const [debugUrl, setDebugUrl] = useState("");
  const [copyStatus, setCopyStatus] = useState("");
  const [imageUrl, setImageUrl] = useState("");

  // ================ 运行期引用 ================
  const abortRef = useRef(null);
  const fullTextRef = useRef("");
  const queueRef = useRef([]);
  const tickingRef = useRef(false);
  const nodeKeyRef = useRef("");
  const hadErrorRef = useRef(false);
  const seenPayloadRef = useRef(new Set());
  const finishedRef = useRef(false);

  const safeJson = (obj) => {
    try { return JSON.stringify(obj); } catch { return String(obj); }
  };

  const dbg = (label, payload) => {
    if (!DEBUG) return;
    const line = payload ? `${label} ${safeJson(payload)}` : label;
    setDebugLines((arr) => [...arr, `[${new Date().toISOString()}] ${line}`]);
    try { console.debug("[XhsRunner]", label, payload || ""); } catch {}
  };


  // =============== 工具函数：解析 & 切片 ===============
  function tryJSON(str) {
    if (typeof str !== "string") return str;
    const raw = str.trim();
    if (!raw) return str;

    try { return JSON.parse(raw); } catch {}

    let fixed = raw;
    fixed = fixed.replace(/\\"content_type"/, '"},"content_type"');
    fixed = fixed.replace(
      /"content":"\{([^]*?)\}\\"content_type"/,
      '"content":"{$1}","content_type"'
    );

    try { return JSON.parse(fixed); } catch {}
    return str;
  }

  function sliceTextIntoChunks(text) {
    if (!text) return [];
    const parts = text.split(/([。！？?.\n\r])/);
    const segs = [];
    for (let i = 0; i < parts.length; i += 2) {
      const content = parts[i] || "";
      const sep = parts[i + 1] || "";
      const piece = content + sep;
      if (!piece) continue;

      if (piece.length <= MAX_CHUNK_CHARS) {
        segs.push(piece);
      } else {
        let tmp = piece;
        const softChars = /([,，。.\s])/;
        while (tmp.length > MAX_CHUNK_CHARS) {
          const slice = tmp.slice(0, MAX_CHUNK_CHARS);
          const lastSpace = slice.lastIndexOf(" ");
          const softMatch = slice.search(softChars) >= 0 ? slice.lastIndexOf(slice.match(softChars)?.[0] || "") : -1;
          const pos = Math.max(lastSpace, softMatch);
          if (pos > MIN_CHUNK_CHARS) {
            segs.push(slice.slice(0, pos + 1));
            tmp = tmp.slice(pos + 1);
          } else {
            segs.push(slice);
            tmp = tmp.slice(MAX_CHUNK_CHARS);
          }
        }
        if (tmp) segs.push(tmp);
      }
    }

    const merged = [];
    for (let i = 0; i < segs.length; i++) {
      const cur = segs[i];
      if (cur.length < MIN_CHUNK_CHARS && i < segs.length - 1) {
        merged.push(cur + segs[++i]);
      } else {
        merged.push(cur);
      }
    }
    return merged;
  }

  function lcp(a, b) {
    const n = Math.min(a.length, b.length);
    let i = 0;
    while (i < n && a.charCodeAt(i) === b.charCodeAt(i)) i++;
    return i;
  }

  function enqueueNewDelta(newFull) {
    const prev = fullTextRef.current;
    const img = getFirstImageUrl(newFull);
    if (img && img !== imageUrl) setImageUrl(img);
    if (prev && newFull.startsWith(prev)) {
      const tail = newFull.slice(prev.length);
      const tailNorm = tail.replace(/^[\s-]+/, "");
      const prevTrim = prev.trim();
      if (tailNorm.startsWith(prevTrim)) {
        dbg("dedupe", { reason: "repeat-block", prevLen: prev.length, newLen: newFull.length });
        fullTextRef.current = newFull;
        return;
      }
    }
    const start = lcp(prev, newFull);
    const delta = newFull.slice(start);
    if (!delta) return;

    const segs = sliceTextIntoChunks(delta);
    if (segs.length) {
      queueRef.current.push(...segs);
      dbg("enqueue", { deltaLen: delta.length, segs: segs.length, queue: queueRef.current.length });
      fullTextRef.current = newFull;
      startRevealLoop();
    }
  }

  function enqueueDelta(delta) {
    if (!delta) return;
    const img = getFirstImageUrl(delta);
    if (img && img !== imageUrl) setImageUrl(img);
    const segs = sliceTextIntoChunks(delta);
    if (!segs.length) return;
    queueRef.current.push(...segs);
    fullTextRef.current = (fullTextRef.current || "") + delta;
    startRevealLoop();
  }

  function startRevealLoop() {
    if (tickingRef.current) return;
    tickingRef.current = true;

    const tick = () => {
      if (queueRef.current.length === 0) {
        tickingRef.current = false;
        return;
      }
      const next = queueRef.current.shift();
      setChunks((arr) => [...arr, next]);
      setTimeout(tick, REVEAL_INTERVAL_MS);
    };

    setTimeout(tick, REVEAL_INTERVAL_MS);
  }

  function extractPayload(dataObj) {
    let inner = dataObj?.content ?? dataObj?.cotent ?? "";
    inner = tryJSON(inner);

    const _title = inner?.title ?? dataObj?.title ?? "";
    const _urls = inner?.urls ?? dataObj?.urls ?? "";
    let _text = inner?.cotent ?? inner?.content ?? inner ?? "";

    if (typeof _text !== "string") {
      try { _text = JSON.stringify(_text, null, 2); } catch {}
    }

    return {
      node_id: dataObj?.node_id,
      node_title: dataObj?.node_title,
      node_type: dataObj?.node_type,
      node_is_finish: !!dataObj?.node_is_finish,
      title: _title,
      urls: _urls,
      text: _text,
    };
  }

  function getFirstImageUrl(input) {
    if (!input) return "";
    const raw = String(input);
    const urls = raw.match(/https?:\/\/[^\s<>"'）)]+/gi) || [];
    const labeled = raw.match(/图片链接[:：]\s*(https?:\/\/[^\s<>"'）)]+)/i);
    if (labeled && labeled[1]) return labeled[1];
    const img = urls.find((u) => /\.(png|jpe?g|gif|webp|bmp|svg)(\?|#|$)/i.test(u));
    if (img) return img;
    const xhs = urls.find((u) => /xhscdn\.com/i.test(u) && /webp|jpg|jpeg|png|image/i.test(u));
    return xhs || "";
  }

  function nodeKeyOf(p) {
    return p.node_id || p.node_title || "unknown";
  }

  function extractFirstUrl(input) {
    const raw = String(input || "").trim();
    if (!raw) return "";
    const match = raw.match(/https?:\/\/[^\s<>"'）)]+/i);
    return match ? match[0] : raw;
  }

  function normalizeXhsUrl(input) {
    const rawInput = String(input || "").trim();
    let raw = extractFirstUrl(rawInput);
    if (!raw) return { ok: false, reason: "请输入有效的小红书链接" };

    if (/^www\.xiaohongshu\.com/i.test(raw) || /^xhslink\.com/i.test(raw)) {
      raw = `https://${raw}`;
    }

    let u;
    try {
      u = new URL(raw);
    } catch {
      return { ok: false, reason: "请输入有效的小红书链接" };
    }

    const host = u.hostname.toLowerCase();
    if (!host.includes("xiaohongshu.com") && !host.includes("xhslink.com")) {
      return { ok: false, reason: "请输入有效的小红书链接" };
    }

    if (host.includes("xhslink.com")) {
      return { ok: true, url: u.toString() };
    }

    const pathname = u.pathname.replace(/\/+$/g, "");
    const segs = pathname.split("/").filter(Boolean);
    const exploreIdx = segs.indexOf("explore");
    const discoveryIdx = segs.indexOf("discovery");
    let noteId = "";
    let prefix = "";

    if (exploreIdx >= 0 && segs[exploreIdx + 1]) {
      noteId = segs[exploreIdx + 1];
      prefix = "explore";
    } else if (discoveryIdx >= 0 && segs[discoveryIdx + 1] === "item" && segs[discoveryIdx + 2]) {
      noteId = segs[discoveryIdx + 2];
      prefix = "discovery/item";
    }

    if (!noteId) {
      return { ok: false, reason: "链接格式不正确，请使用笔记详情链接" };
    }

    const normalizedBase = `https://www.xiaohongshu.com/${prefix}/${noteId}`;
    const shouldKeepQuery =
      u.searchParams.has("xsec_token") ||
      u.searchParams.has("xsec_source") ||
      /xsec_token/i.test(rawInput) ||
      /xsec_source/i.test(rawInput);
    const normalized = shouldKeepQuery ? `${normalizedBase}${u.search}` : normalizedBase;
    return { ok: true, url: normalized };
  }

  async function handleRunStream(e) {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setError("");
    setTitle("");
    setLink("");
    setChunks([]);
    setStreamRaw("");
    setDebugLines([]);
    setRawInput(url);
    setSentUrl("");
    setLastEventRaw("");
    setDebugUrl("");
    setCopyStatus("");
    setImageUrl("");
    fullTextRef.current = "";
    queueRef.current = [];
    tickingRef.current = false;
    nodeKeyRef.current = "";
    hadErrorRef.current = false;
    seenPayloadRef.current = new Set();
    finishedRef.current = false;

    const normalized = normalizeXhsUrl(url);
    if (!normalized.ok) {
      setLoading(false);
      setError(normalized.reason);
      return;
    }
    if (normalized.url !== url) setUrl(normalized.url);
    setSentUrl(normalized.url);

    const token = localStorage.getItem("token");
    if (!token) {
      setLoading(false);
      setError("请先登录后再调用");
      return;
    }

    const makeIdemKey = () => {
      try { return crypto.randomUUID(); } catch {}
      return (Date.now().toString(36) + Math.random().toString(36).slice(2)).slice(0, 64);
    };

    const ac = new AbortController();
    abortRef.current = ac;
    const nextPaint = () => new Promise(requestAnimationFrame);
    let streamSucceeded = false;

    try {
      dbg("start", { url: normalized.url });
      const r = await apiFetch("/api/coze/xhs/stream", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "text/event-stream",
          "Authorization": `Bearer ${token}`,
          "Idempotency-Key": makeIdemKey(),
        },
        body: { input: normalized.url },
        raw: true,
        signal: ac.signal,
      });

      if (r.status === 401) {
        setError("未登录或登录已过期，请重新登录");
        setLoading(false);
        return;
      }
      if (!r.ok) {
        const maybe = await r.text().catch(() => "");
        if (DEBUG) console.warn("[XHS] http error", r.status, maybe);
        throw new Error(maybe || r.statusText);
      }

      const reader = r.body?.getReader();
      const decoder = new TextDecoder();
      if (!reader) throw new Error("当前环境不支持可读流");

      let buffer = "";

      const flushFrame = async (frame) => {
        if (!frame.trim()) return;
        if (finishedRef.current) return;
        if (DEBUG) {
          setStreamRaw((s) => s + frame + "\n\n");
          dbg("frame", { size: frame.length });
        }
        setLastEventRaw(frame);

        const lines = frame.split(/\r?\n/);
        let eventName = "message";
        const dataLines = [];
        for (const ln of lines) {
          if (ln.startsWith("event:")) eventName = ln.slice(6).trim();
          else if (ln.startsWith("data:")) dataLines.push(ln.slice(5).trim());
        }

        const dataStr = dataLines.join("\n");
        const dataObj = tryJSON(dataStr);
        if (DEBUG) console.log("[XHS] event", { eventName, dataStr });

        if (eventName === "conversation.message.delta") {
          const delta = dataObj?.content || "";
          if (delta) enqueueDelta(delta);
          await nextPaint();
          return;
        }
        if (eventName === "conversation.message.completed" || eventName === "conversation.chat.completed") {
          dbg("event:completed", { eventName });
          finishedRef.current = true;
          await nextPaint();
          return;
        }

        switch (eventName) {
          case "PING":
            dbg("event:PING");
            break;
          case "Error":
          case "error":
            hadErrorRef.current = true;
            dbg("event:Error", dataObj);
            if (DEBUG) console.warn("[XHS] error event", dataObj);
            setError(dataObj?.error_message || dataObj?.message || "上游错误");
            await nextPaint();
            break;
          case "Done":
          case "done":
            dbg("event:Done");
            if (dataObj?.debug_url) setDebugUrl(dataObj.debug_url);
            finishedRef.current = true;
            await nextPaint();
            break;
          default: {
            if (dataObj?.msg_type === "generate_answer_finish" || dataObj?.msg_type === "finish") {
              dbg("event:FinishMsg", { msg_type: dataObj?.msg_type });
              finishedRef.current = true;
              await nextPaint();
              break;
            }
            if (dataObj?.type === "verbose") {
              dbg("event:verbose");
              await nextPaint();
              break;
            }
            const parsed = extractPayload(
              (eventName === "Message" || eventName === "message") ? dataObj :
              (typeof dataObj === "object" ? dataObj : { content: dataStr })
            );
            dbg("event:Message", { eventName, node: nodeKeyOf(parsed), title: !!parsed.title, urls: !!parsed.urls, textLen: (parsed.text || "").length });
            if (DEBUG) console.log("[XHS] parsed", parsed);

            if (parsed.title) setTitle(parsed.title);
            if (parsed.urls) setLink(parsed.urls);

            const key = nodeKeyOf(parsed);
            nodeKeyRef.current = key;

            const newFull = parsed.text || "";
            const signature = JSON.stringify({
              title: parsed.title || "",
              urls: parsed.urls || "",
              text: newFull || "",
            });
            if (seenPayloadRef.current.has(signature)) {
              dbg("skip:dup_payload");
              await nextPaint();
              break;
            }
            seenPayloadRef.current.add(signature);
            if (newFull && newFull === fullTextRef.current) {
              dbg("skip:dup_full_text", { len: newFull.length });
              await nextPaint();
              break;
            }
            if (newFull) enqueueNewDelta(newFull);

            await nextPaint();
          }
        }
      };

      while (true) {
        const { value, done } = await reader.read();
        if (done) { if (buffer) await flushFrame(buffer); break; }
        buffer += decoder.decode(value, { stream: true });

        const frames = buffer.split(/\r?\n\r?\n/);
        buffer = frames.pop() || "";
        for (const fr of frames) await flushFrame(fr);
      }

      if (!hadErrorRef.current) {
        streamSucceeded = true;
      }
    } catch (err) {
      dbg("exception", { name: err?.name, message: err?.message });
      if (err && err.name !== "AbortError") setError(err.message || "流式请求失败");
    } finally {
      dbg("finish", { streamSucceeded, hadError: hadErrorRef.current });
      setLoading(false);
      abortRef.current = null;

      if (streamSucceeded) {
        try { window.dispatchEvent(new CustomEvent('wallet:refresh')); } catch {}
      }
    }
  }

  function handleCancel() {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = null;
  }

  async function handleCopyDebug() {
    const payload = [
      `原始输入: ${rawInput || "-"}`,
      `最终发送 URL: ${sentUrl || "-"}`,
      `debug_url: ${debugUrl || "-"}`,
      "最近一条事件:",
      lastEventRaw || "-",
    ].join("\n");
    try {
      await navigator.clipboard.writeText(payload);
      setCopyStatus("已复制");
    } catch {
      setCopyStatus("复制失败");
    }
    setTimeout(() => setCopyStatus(""), 1500);
  }

  return (
    <main className="coze-wrap">
      <div className="coze-header">
        <BackButton />
        <div>
          <h1 className="coze-title">提取小红书爆款内容</h1>
          <p className="coze-subtitle">输入小红书笔记链接，经服务端代理解析并流式展示结果。</p>
        </div>
      </div>

      <form className="coze-form" onSubmit={handleRunStream}>
        <input
          className="coze-input"
          type="url"
          required
          placeholder="例如：https://www.xiaohongshu.com/explore/..."
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
        <div className="coze-actions">
          <button className="coze-btn" type="submit" disabled={loading}>
            {loading ? "运行中…" : "开始生成"}
          </button>
          <button className="coze-btn" type="button" onClick={handleCancel} disabled={!abortRef.current}>
            取消
          </button>
        </div>
      </form>

      {error && <div className="coze-error">错误：{error}</div>}

      {(title || link) && (
        <section className="coze-card" style={{ marginTop: 12 }}>
          {title && <p><b>标题：</b>{title}</p>}
          {link && (
            <p><b>链接：</b>
              <a href={link} target="_blank" rel="noreferrer">{link}</a>
            </p>
          )}
        </section>
      )}

      {imageUrl && (
        <section className="coze-card" style={{ marginTop: 12 }}>
          <p><b>缩略图：</b></p>
          <div style={{ marginTop: 10, display: "flex", justifyContent: "center" }}>
            <img
              src={imageUrl}
              alt="封面预览"
              style={{
                width: "220px",
                maxWidth: "100%",
                height: "auto",
                maxHeight: "300px",
                objectFit: "cover",
                borderRadius: 12,
                border: "1px solid rgba(255,255,255,0.08)",
                boxShadow: "0 10px 24px rgba(0,0,0,0.35)"
              }}
              onError={() => setImageUrl("")}
            />
          </div>
        </section>
      )}

      <section className="coze-card" style={{ marginTop: 12 }}>
        <h3>正文</h3>
        {loading && chunks.length === 0 && (
          <div className="coze-result" style={{ opacity: 0.8 }}>
            正在生成中，请稍候…
          </div>
        )}
        {!loading && chunks.length === 0 && (
          <div className="coze-result" style={{ opacity: 0.6 }}>
            暂无内容
          </div>
        )}
        {chunks.length > 0 && (
          <div className="coze-result">
            {chunks.join("")}
          </div>
        )}
      </section>

    </main>
  );
}




